/**
 * PS Tracer - Par Seeker Golf Shot Tracer
 * 
 * FLOW:
 * 1. Upload video → Auto-plays and loops within trim range
 * 2. AI automatically scans for ball launch and trajectory
 * 3. AI shows predicted path (or indicates manual input needed)
 * 4. User can adjust Impact/Apex/Landing markers while video plays
 * 5. Generate tracer with glowing path
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { 
  Loader2, 
  X, 
  Play, 
  Pause,
  Target,
  MapPin,
  Sparkles,
  RotateCcw,
  Check,
  Video,
  ZoomIn,
  ZoomOut,
  Circle,
  Crosshair
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Tracer color options
const TRACER_COLORS = {
  blue: { name: 'Blue', hex: '#00BFFF', rgb: '0,191,255' },
  yellow: { name: 'Yellow', hex: '#FFFF00', rgb: '255,255,0' },
  red: { name: 'Red', hex: '#FF3333', rgb: '255,51,51' },
  cyan: { name: 'Cyan', hex: '#00FFFF', rgb: '0,255,255' },
  green: { name: 'Green', hex: '#00FF00', rgb: '0,255,0' },
  white: { name: 'White', hex: '#FFFFFF', rgb: '255,255,255' },
};

const ShotTracerModal = ({ isOpen, onClose, onVideoCreated }) => {
  // Mode: 'upload' | 'editor' | 'processing' | 'done'
  const [mode, setMode] = useState('upload');
  
  // Video state
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoSize, setVideoSize] = useState({ width: 0, height: 0 });
  
  // Trim state
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [thumbnails, setThumbnails] = useState([]);
  const [draggingHandle, setDraggingHandle] = useState(null);
  const trimContainerRef = useRef(null);
  
  // Zoom state
  const [zoom, setZoom] = useState(1);
  
  // Marker state - Impact, Apex, Landing
  const [impactPoint, setImpactPoint] = useState(null);
  const [apexPoint, setApexPoint] = useState(null);
  const [landingPoint, setLandingPoint] = useState(null);
  const [tracerColor, setTracerColor] = useState('blue');
  const [draggingMarker, setDraggingMarker] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null); // 'impact' | 'apex' | 'landing' | null
  
  // AI state
  const [aiPhase, setAiPhase] = useState('idle'); // 'idle' | 'scanning' | 'calculating' | 'complete' | 'manual'
  const [aiProgress, setAiProgress] = useState(0);
  const [showMarkers, setShowMarkers] = useState(false);
  const [aiAttempts, setAiAttempts] = useState(0);
  const [lastProgress, setLastProgress] = useState(0);
  const [stagnantCount, setStagnantCount] = useState(0);
  const aiTimeoutRef = useRef(null);
  const progressCheckRef = useRef(null);
  
  // Trajectory data from AI
  const [trajectoryPoints, setTrajectoryPoints] = useState([]);
  const [analysisData, setAnalysisData] = useState(null);
  
  // Processing state
  const [processingStatus, setProcessingStatus] = useState('');
  const [outputUrl, setOutputUrl] = useState(null);
  
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Generate thumbnails from video
  const generateThumbnails = useCallback(async (video, duration) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const thumbCount = 10;
    const thumbWidth = 50;
    const thumbHeight = 70;
    canvas.width = thumbWidth;
    canvas.height = thumbHeight;
    
    const thumbs = [];
    const interval = duration / thumbCount;
    
    for (let i = 0; i < thumbCount; i++) {
      const time = i * interval;
      video.currentTime = time;
      
      await new Promise(resolve => {
        const onSeeked = () => {
          ctx.drawImage(video, 0, 0, thumbWidth, thumbHeight);
          thumbs.push({
            time,
            dataUrl: canvas.toDataURL('image/jpeg', 0.5)
          });
          video.removeEventListener('seeked', onSeeked);
          resolve();
        };
        video.addEventListener('seeked', onSeeked);
      });
    }
    
    video.currentTime = 0;
    return thumbs;
  }, []);

  // Generate thumbnails when video loads
  useEffect(() => {
    if (videoRef.current && videoDuration > 0 && thumbnails.length === 0 && videoUrl) {
      const video = videoRef.current;
      if (video.readyState >= 2) {
        generateThumbnails(video, videoDuration).then(setThumbnails);
      }
    }
  }, [videoDuration, videoUrl, thumbnails.length, generateThumbnails]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }
    
    const url = URL.createObjectURL(file);
    setVideoFile(file);
    setVideoUrl(url);
    setMode('editor');
    setImpactPoint(null);
    setApexPoint(null);
    setLandingPoint(null);
    setZoom(1);
    setThumbnails([]);
    setActiveMarker(null);
    setAiPhase('idle');
    setShowMarkers(false);
  };

  // Handle video metadata loaded - Start AI scanning
  const handleVideoLoad = () => {
    if (videoRef.current) {
      const duration = videoRef.current.duration;
      setVideoDuration(duration);
      setTrimStart(0);
      setTrimEnd(duration);
      setVideoSize({
        width: videoRef.current.videoWidth,
        height: videoRef.current.videoHeight
      });
      
      // Start auto-play and AI scanning
      const video = videoRef.current;
      setTimeout(() => {
        video.play().then(() => {
          setIsPlaying(true);
          startAIScanning();
        }).catch(() => {
          setIsPlaying(false);
          startAIScanning();
        });
      }, 300);
    }
  };

  // AI Scanning process - calls actual backend with retry and stagnation detection
  const startAIScanning = async (attemptNum = 1) => {
    if (!videoFile) {
      showManualTools();
      return;
    }
    
    // Check if we've exceeded max attempts
    if (attemptNum > 2) {
      console.log('AI scan: Max attempts reached, showing manual tools');
      showManualTools();
      toast.info('AI could not detect trajectory. Mark points manually.');
      return;
    }
    
    setAiAttempts(attemptNum);
    setAiPhase('scanning');
    setAiProgress(0);
    setLastProgress(0);
    setStagnantCount(0);
    
    // Clear any existing timeouts
    if (aiTimeoutRef.current) clearTimeout(aiTimeoutRef.current);
    if (progressCheckRef.current) clearInterval(progressCheckRef.current);
    
    // Progress animation while scanning
    let currentProgress = 0;
    const progressInterval = setInterval(() => {
      currentProgress += 2;
      if (currentProgress > 95) currentProgress = 95; // Cap at 95%
      setAiProgress(currentProgress);
    }, 100);
    
    // Stagnation check - if progress doesn't change for 5 seconds, retry or show manual
    progressCheckRef.current = setInterval(() => {
      setAiProgress(prev => {
        if (prev === lastProgress) {
          setStagnantCount(c => {
            const newCount = c + 1;
            if (newCount >= 5) {
              // Stagnant for 5 seconds
              clearInterval(progressInterval);
              clearInterval(progressCheckRef.current);
              if (attemptNum < 2) {
                console.log(`AI scan stagnant at ${prev}%, retrying (attempt ${attemptNum + 1})`);
                toast.info(`Retrying scan... (attempt ${attemptNum + 1})`);
                startAIScanning(attemptNum + 1);
              } else {
                showManualTools();
                toast.info('Scan stalled. Mark points manually.');
              }
            }
            return newCount;
          });
        } else {
          setLastProgress(prev);
          setStagnantCount(0);
        }
        return prev;
      });
    }, 1000);
    
    // Overall timeout - 15 seconds max per attempt
    aiTimeoutRef.current = setTimeout(() => {
      clearInterval(progressInterval);
      clearInterval(progressCheckRef.current);
      if (attemptNum < 2) {
        console.log(`AI scan timeout, retrying (attempt ${attemptNum + 1})`);
        toast.info(`Scan timeout. Retrying... (attempt ${attemptNum + 1})`);
        startAIScanning(attemptNum + 1);
      } else {
        showManualTools();
        toast.info('Scan timeout. Mark points manually.');
      }
    }, 15000);
    
    try {
      const formData = new FormData();
      formData.append('video', videoFile);
      formData.append('trim_start', trimStart.toString());
      formData.append('trim_end', trimEnd.toString());
      
      setAiPhase('calculating');
      
      const response = await fetch(`${API}/shot-tracer/scan`, {
        method: 'POST',
        body: formData
      });
      
      // Clear intervals and timeout
      clearInterval(progressInterval);
      clearInterval(progressCheckRef.current);
      clearTimeout(aiTimeoutRef.current);
      
      if (response.ok) {
        const result = await response.json();
        
        // Check if result is complete (has all required points)
        const hasImpact = result.impact && result.impact.x !== undefined;
        const hasLanding = result.landing && result.landing.x !== undefined;
        const isComplete = result.success && hasImpact && hasLanding;
        
        // Calculate completeness percentage
        let completeness = 0;
        if (hasImpact) completeness += 40;
        if (result.apex) completeness += 20;
        if (hasLanding) completeness += 40;
        
        setAiProgress(100);
        
        if (isComplete && completeness >= 98) {
          // AI detected points successfully - set them
          setImpactPoint({
            x: result.impact.x,
            y: result.impact.y,
            normalizedX: result.impact.normalizedX,
            normalizedY: result.impact.normalizedY,
            frame: result.impact.frame,
            time: result.impact.time
          });
          
          if (result.apex) {
            setApexPoint({
              x: result.apex.x,
              y: result.apex.y,
              normalizedX: result.apex.normalizedX,
              normalizedY: result.apex.normalizedY,
              frame: result.apex.frame,
              time: result.apex.time
            });
          }
          
          setLandingPoint({
            x: result.landing.x,
            y: result.landing.y,
            normalizedX: result.landing.normalizedX,
            normalizedY: result.landing.normalizedY,
            frame: result.landing.frame,
            time: result.landing.time
          });
          
          // Store trajectory points and analysis data
          if (result.trajectory_points) {
            setTrajectoryPoints(result.trajectory_points);
          }
          if (result.analysis) {
            setAnalysisData(result.analysis);
          }
          
          setAiPhase('complete');
          setShowMarkers(true);
          setActiveMarker(null);
          
          // Show detection stats in toast
          const stats = result.analysis || {};
          const speedInfo = stats.ball_speed_mph ? `~${stats.ball_speed_mph} mph` : '';
          const angleInfo = stats.launch_angle_deg ? `${stats.launch_angle_deg}° launch` : '';
          const dirInfo = stats.direction || '';
          const infoStr = [speedInfo, angleInfo, dirInfo].filter(Boolean).join(', ');
          toast.success(`Ball tracked! ${infoStr || 'Adjust markers to modify arc.'}`);
        } else if (attemptNum < 2) {
          // Incomplete result, retry
          console.log(`AI scan incomplete (${completeness}%), retrying`);
          toast.info(`Detection incomplete. Retrying... (attempt ${attemptNum + 1})`);
          startAIScanning(attemptNum + 1);
        } else {
          // Max attempts reached, show manual tools
          showManualTools();
          toast.info(result.message || 'Mark Impact, Apex, and Landing to define arc');
        }
      } else {
        throw new Error('Scan request failed');
      }
    } catch (error) {
      console.error('AI scan error:', error);
      clearInterval(progressInterval);
      clearInterval(progressCheckRef.current);
      clearTimeout(aiTimeoutRef.current);
      
      if (attemptNum < 2) {
        console.log(`AI scan error, retrying (attempt ${attemptNum + 1})`);
        toast.info(`Scan error. Retrying... (attempt ${attemptNum + 1})`);
        startAIScanning(attemptNum + 1);
      } else {
        showManualTools();
        toast.info('AI unavailable. Mark points manually.');
      }
    }
  };
  
  // Show manual marker tools
  const showManualTools = () => {
    // Clear any pending operations
    if (aiTimeoutRef.current) clearTimeout(aiTimeoutRef.current);
    if (progressCheckRef.current) clearInterval(progressCheckRef.current);
    
    setAiPhase('manual');
    setShowMarkers(true);
    setActiveMarker('impact');
    setAiProgress(0);
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (aiTimeoutRef.current) clearTimeout(aiTimeoutRef.current);
      if (progressCheckRef.current) clearInterval(progressCheckRef.current);
    };
  }, []);

  // Toggle play/pause
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Handle time update with trim loop
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const time = videoRef.current.currentTime;
      setCurrentTime(time);
      
      // Loop within trim range
      if (time >= trimEnd) {
        videoRef.current.currentTime = trimStart;
      } else if (time < trimStart) {
        videoRef.current.currentTime = trimStart;
      }
    }
  };

  // Seek to time
  const seekTo = (time) => {
    if (videoRef.current) {
      const clampedTime = Math.max(trimStart, Math.min(trimEnd, time));
      videoRef.current.currentTime = clampedTime;
      setCurrentTime(clampedTime);
    }
  };

  // Zoom controls
  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.5, 4));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.5, 1));

  // Handle trim handle drag with video scrubbing
  const handleTrimHandleDrag = useCallback((e) => {
    if (!draggingHandle || !trimContainerRef.current || !videoRef.current) return;
    
    e.preventDefault();
    const rect = trimContainerRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const relativeX = Math.max(0, Math.min(rect.width, clientX - rect.left));
    const percentage = relativeX / rect.width;
    let newTime = percentage * videoDuration;
    
    // Snap to edges
    const snapThreshold = 0.02 * videoDuration;
    if (newTime < snapThreshold) newTime = 0;
    if (newTime > videoDuration - snapThreshold) newTime = videoDuration;
    
    if (draggingHandle === 'start') {
      const maxStart = trimEnd - 0.5;
      const clampedTime = Math.max(0, Math.min(newTime, maxStart));
      setTrimStart(clampedTime);
      videoRef.current.currentTime = clampedTime;
    } else if (draggingHandle === 'end') {
      const minEnd = trimStart + 0.5;
      const clampedTime = Math.min(videoDuration, Math.max(newTime, minEnd));
      setTrimEnd(clampedTime);
      videoRef.current.currentTime = clampedTime;
    }
  }, [draggingHandle, videoDuration, trimStart, trimEnd]);

  const handleTrimHandleEnd = useCallback(() => {
    setDraggingHandle(null);
    // Resume playback after trim adjustment
    if (videoRef.current && !isPlaying) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  }, [isPlaying]);

  // Add listeners for trim handle dragging
  useEffect(() => {
    if (draggingHandle) {
      window.addEventListener('mousemove', handleTrimHandleDrag);
      window.addEventListener('mouseup', handleTrimHandleEnd);
      window.addEventListener('touchmove', handleTrimHandleDrag, { passive: false });
      window.addEventListener('touchend', handleTrimHandleEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleTrimHandleDrag);
        window.removeEventListener('mouseup', handleTrimHandleEnd);
        window.removeEventListener('touchmove', handleTrimHandleDrag);
        window.removeEventListener('touchend', handleTrimHandleEnd);
      };
    }
  }, [draggingHandle, handleTrimHandleDrag, handleTrimHandleEnd]);

  // Convert screen coordinates to normalized video coordinates
  const screenToNormalizedCoords = (clientX, clientY) => {
    if (!containerRef.current || !videoSize.width) return null;
    
    const rect = containerRef.current.getBoundingClientRect();
    const videoRatio = videoSize.width / videoSize.height;
    const containerRatio = rect.width / rect.height;
    
    let displayWidth, displayHeight, offsetX, offsetY;
    
    if (videoRatio > containerRatio) {
      displayWidth = rect.width;
      displayHeight = rect.width / videoRatio;
      offsetX = 0;
      offsetY = (rect.height - displayHeight) / 2;
    } else {
      displayHeight = rect.height;
      displayWidth = rect.height * videoRatio;
      offsetX = (rect.width - displayWidth) / 2;
      offsetY = 0;
    }
    
    const zoomedWidth = displayWidth * zoom;
    const zoomedHeight = displayHeight * zoom;
    const zoomedOffsetX = offsetX + (displayWidth - zoomedWidth) / 2;
    const zoomedOffsetY = offsetY + (displayHeight - zoomedHeight) / 2;
    
    const clickX = clientX - rect.left;
    const clickY = clientY - rect.top;
    
    const normalizedX = (clickX - zoomedOffsetX) / zoomedWidth;
    const normalizedY = (clickY - zoomedOffsetY) / zoomedHeight;
    
    if (normalizedX < 0 || normalizedX > 1 || normalizedY < 0 || normalizedY > 1) {
      return null;
    }
    
    return { normalizedX, normalizedY };
  };

  // Handle tap on video to mark point (video keeps playing)
  const handleVideoTap = (e) => {
    if (draggingMarker || !activeMarker || !showMarkers) return;
    if (!videoRef.current || !containerRef.current) return;
    
    // Don't pause - let video keep playing
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const coords = screenToNormalizedCoords(clientX, clientY);
    
    if (!coords) return;
    
    const { normalizedX, normalizedY } = coords;
    const videoX = Math.round(normalizedX * videoSize.width);
    const videoY = Math.round(normalizedY * videoSize.height);
    const fps = 30;
    const frame = Math.round(currentTime * fps);
    
    const point = {
      x: videoX,
      y: videoY,
      normalizedX,
      normalizedY,
      time: currentTime,
      frame
    };
    
    if (activeMarker === 'impact') {
      setImpactPoint(point);
      toast.success('Impact marked');
    } else if (activeMarker === 'apex') {
      setApexPoint(point);
      toast.success('Apex marked');
    } else if (activeMarker === 'landing') {
      setLandingPoint(point);
      toast.success('Landing marked');
    }
    
    // Auto-advance to next marker
    if (activeMarker === 'impact' && !apexPoint) {
      setActiveMarker('apex');
    } else if (activeMarker === 'apex' && !landingPoint) {
      setActiveMarker('landing');
    } else {
      setActiveMarker(null);
    }
  };

  // Handle marker drag (while video plays)
  const handleMarkerDragStart = (e, markerType) => {
    e.stopPropagation();
    e.preventDefault();
    setDraggingMarker(markerType);
  };

  const handleMarkerDrag = useCallback((e) => {
    if (!draggingMarker) return;
    
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const coords = screenToNormalizedCoords(clientX, clientY);
    
    if (!coords) return;
    
    const { normalizedX, normalizedY } = coords;
    const videoX = Math.round(normalizedX * videoSize.width);
    const videoY = Math.round(normalizedY * videoSize.height);
    
    const updatePoint = (point) => ({
      ...point,
      x: videoX,
      y: videoY,
      normalizedX,
      normalizedY
    });
    
    if (draggingMarker === 'impact' && impactPoint) {
      setImpactPoint(updatePoint(impactPoint));
    } else if (draggingMarker === 'apex' && apexPoint) {
      setApexPoint(updatePoint(apexPoint));
    } else if (draggingMarker === 'landing' && landingPoint) {
      setLandingPoint(updatePoint(landingPoint));
    }
  }, [draggingMarker, impactPoint, apexPoint, landingPoint, videoSize, zoom]);

  const handleMarkerDragEnd = () => {
    setDraggingMarker(null);
  };

  useEffect(() => {
    if (draggingMarker) {
      window.addEventListener('mousemove', handleMarkerDrag);
      window.addEventListener('mouseup', handleMarkerDragEnd);
      window.addEventListener('touchmove', handleMarkerDrag, { passive: false });
      window.addEventListener('touchend', handleMarkerDragEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleMarkerDrag);
        window.removeEventListener('mouseup', handleMarkerDragEnd);
        window.removeEventListener('touchmove', handleMarkerDrag);
        window.removeEventListener('touchend', handleMarkerDragEnd);
      };
    }
  }, [draggingMarker, handleMarkerDrag]);

  // Reset markers
  const resetMarkers = () => {
    setImpactPoint(null);
    setApexPoint(null);
    setLandingPoint(null);
    setActiveMarker('impact');
    setZoom(1);
  };

  // Format time
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00.0';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 10);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms}`;
  };

  // Generate tracer
  const generateTracer = async () => {
    if (!videoFile || !impactPoint || !landingPoint) return;
    
    setMode('processing');
    setProcessingStatus('Uploading video...');
    
    try {
      const formData = new FormData();
      formData.append('video', videoFile);
      formData.append('start_x', impactPoint.x.toString());
      formData.append('start_y', impactPoint.y.toString());
      formData.append('start_frame', impactPoint.frame.toString());
      formData.append('end_x', landingPoint.x.toString());
      formData.append('end_y', landingPoint.y.toString());
      formData.append('end_frame', landingPoint.frame.toString());
      
      if (apexPoint) {
        formData.append('apex_x', apexPoint.x.toString());
        formData.append('apex_y', apexPoint.y.toString());
        formData.append('apex_frame', apexPoint.frame.toString());
      }
      
      formData.append('tracer_color', TRACER_COLORS[tracerColor].rgb);
      formData.append('video_width', videoSize.width.toString());
      formData.append('video_height', videoSize.height.toString());
      
      if (trimStart > 0 || trimEnd < videoDuration) {
        formData.append('trim_start', trimStart.toString());
        formData.append('trim_end', trimEnd.toString());
      }
      
      const response = await fetch(`${API}/shot-tracer/create`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to start processing');
      }
      
      const data = await response.json();
      const jobId = data.job_id;
      
      setProcessingStatus('Processing video...');
      const result = await pollJobStatus(jobId);
      
      setOutputUrl(result.video_url);
      setMode('done');
      toast.success('Shot tracer created!');
      
    } catch (err) {
      toast.error(err.message || 'Failed to create tracer');
      setMode('editor');
    }
  };

  // Poll for job completion
  const pollJobStatus = async (jobId, maxAttempts = 60) => {
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const response = await fetch(`${API}/shot-tracer/status/${jobId}`);
        if (!response.ok) throw new Error('Failed to check status');
        
        const data = await response.json();
        setProcessingStatus(data.progress || 'Processing...');
        
        if (data.status === 'completed') return data;
        if (data.status === 'failed') throw new Error(data.error || 'Processing failed');
        
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (err) {
        if (i === maxAttempts - 1) throw err;
      }
    }
    throw new Error('Processing timeout');
  };

  // Use in post
  const handleUseInPost = () => {
    if (outputUrl) {
      const fullUrl = outputUrl.startsWith('http') 
        ? outputUrl 
        : `${process.env.REACT_APP_BACKEND_URL}${outputUrl}`;
      onVideoCreated?.(fullUrl);
      onClose();
    }
  };

  // Reset all
  const resetAll = () => {
    setMode('upload');
    setVideoFile(null);
    setVideoUrl(null);
    setImpactPoint(null);
    setApexPoint(null);
    setLandingPoint(null);
    setOutputUrl(null);
    setCurrentTime(0);
    setIsPlaying(false);
    setThumbnails([]);
    setTrimStart(0);
    setTrimEnd(0);
    setActiveMarker(null);
    setZoom(1);
    setAiPhase('idle');
    setShowMarkers(false);
    setTrajectoryPoints([]);
    setAnalysisData(null);
    setAiAttempts(0);
  };

  const canGenerate = impactPoint && landingPoint;

  // Render trajectory path - arc is defined by Impact, Apex (control), and Landing
  // Moving any point modifies the arc shape
  const renderTrajectoryPath = () => {
    if (!impactPoint) return null;
    
    // Need at least impact and landing to draw
    if (!landingPoint && !apexPoint) return null;
    
    // Calculate path based on available points
    let pathD;
    const impact = impactPoint;
    const landing = landingPoint || apexPoint; // Use apex as endpoint if no landing yet
    
    if (apexPoint && landingPoint) {
      // Full 3-point curve: Impact -> Apex (control point) -> Landing
      // Using quadratic bezier where apex is the control point
      pathD = `M ${impact.normalizedX * 100}% ${impact.normalizedY * 100}%
               Q ${apexPoint.normalizedX * 100}% ${apexPoint.normalizedY * 100}%
               ${landing.normalizedX * 100}% ${landing.normalizedY * 100}%`;
    } else if (apexPoint) {
      // Impact to Apex only (landing not set yet)
      pathD = `M ${impact.normalizedX * 100}% ${impact.normalizedY * 100}%
               L ${apexPoint.normalizedX * 100}% ${apexPoint.normalizedY * 100}%`;
    } else if (landingPoint) {
      // Impact to Landing with auto-calculated apex (when apex not set)
      const midX = (impact.normalizedX + landing.normalizedX) / 2;
      // Auto apex: highest point is above the midpoint
      const baseY = Math.min(impact.normalizedY, landing.normalizedY);
      const arcHeight = Math.abs(landing.normalizedX - impact.normalizedX) * 0.35;
      const autoApexY = Math.max(0.05, baseY - arcHeight);
      
      pathD = `M ${impact.normalizedX * 100}% ${impact.normalizedY * 100}%
               Q ${midX * 100}% ${autoApexY * 100}%
               ${landing.normalizedX * 100}% ${landing.normalizedY * 100}%`;
    }
    
    if (!pathD) return null;
    
    return (
      <svg className="absolute inset-0 w-full h-full pointer-events-none z-5">
        <defs>
          <filter id="tracer-glow">
            <feGaussianBlur stdDeviation="6" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <linearGradient id="tracer-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={TRACER_COLORS[tracerColor].hex} stopOpacity="1"/>
            <stop offset="50%" stopColor={TRACER_COLORS[tracerColor].hex} stopOpacity="0.8"/>
            <stop offset="100%" stopColor={TRACER_COLORS[tracerColor].hex} stopOpacity="0.6"/>
          </linearGradient>
        </defs>
        {/* Main glowing arc */}
        <path
          d={pathD}
          stroke={TRACER_COLORS[tracerColor].hex}
          strokeWidth="5"
          fill="none"
          filter="url(#tracer-glow)"
          strokeLinecap="round"
          className="animate-pulse"
        />
        {/* Inner bright line */}
        <path
          d={pathD}
          stroke="white"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          opacity="0.5"
        />
        {/* Show tracked trajectory points as small dots */}
        {trajectoryPoints.length > 0 && trajectoryPoints.map((point, i) => (
          <circle
            key={i}
            cx={`${point.normalizedX * 100}%`}
            cy={`${point.normalizedY * 100}%`}
            r="2"
            fill={TRACER_COLORS[tracerColor].hex}
            opacity={0.6}
          />
        ))}
      </svg>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="!fixed !inset-0 !w-full !h-full !max-w-none !max-h-none !m-0 !p-0 !bg-black !border-0 !rounded-none !translate-x-0 !translate-y-0 !top-0 !left-0 [&>button]:hidden"
      >
        {/* UPLOAD MODE */}
        {mode === 'upload' && (
          <div className="w-full h-full flex flex-col bg-black">
            <div className="flex items-center justify-between px-4 py-3">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-yellow-400" />
                PS Tracer
              </h2>
              <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex-1 flex flex-col items-center justify-center p-6">
              <div 
                className="border-2 border-dashed border-gray-700 rounded-xl p-12 text-center cursor-pointer hover:border-yellow-500/50 transition-colors w-full max-w-md"
                onClick={() => fileInputRef.current?.click()}
              >
                <Video className="w-16 h-16 mx-auto text-gray-500 mb-4" />
                <p className="text-gray-300 text-lg mb-2">Tap to upload swing video</p>
                <p className="text-gray-500 text-sm">MP4, MOV, or WebM</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          </div>
        )}

        {/* EDITOR MODE */}
        {mode === 'editor' && videoUrl && (
          <div className="w-full h-full flex flex-col bg-black">
            {/* Video container */}
            <div 
              ref={containerRef}
              className="flex-1 relative bg-black overflow-hidden"
              onClick={showMarkers && activeMarker ? handleVideoTap : undefined}
              style={{ cursor: showMarkers && activeMarker ? 'crosshair' : 'default' }}
            >
              {/* Video */}
              <div 
                className="w-full h-full flex items-center justify-center"
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: 'center center'
                }}
              >
                <video
                  ref={videoRef}
                  src={videoUrl}
                  className="max-w-full max-h-full object-contain"
                  playsInline
                  webkit-playsinline="true"
                  muted
                  loop
                  preload="auto"
                  onLoadedMetadata={handleVideoLoad}
                  onLoadedData={handleVideoLoad}
                  onTimeUpdate={handleTimeUpdate}
                />
              </div>
              
              {/* Trajectory path */}
              {showMarkers && renderTrajectoryPath()}
              
              {/* Impact marker */}
              {showMarkers && impactPoint && (
                <div 
                  className={`absolute w-10 h-10 -ml-5 -mt-5 ${draggingMarker === 'impact' ? 'cursor-grabbing' : 'cursor-grab'} z-10`}
                  style={{ 
                    left: `${impactPoint.normalizedX * 100}%`, 
                    top: `${impactPoint.normalizedY * 100}%`,
                    transform: `scale(${1/zoom})`
                  }}
                  onMouseDown={(e) => handleMarkerDragStart(e, 'impact')}
                  onTouchStart={(e) => handleMarkerDragStart(e, 'impact')}
                >
                  <div className="w-full h-full rounded-full border-3 border-green-400 bg-green-400/30 flex items-center justify-center shadow-lg shadow-green-400/50">
                    <Target className="w-5 h-5 text-green-400" />
                  </div>
                  <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[9px] text-green-400 font-bold whitespace-nowrap bg-black/80 px-1.5 py-0.5 rounded">
                    IMPACT
                  </span>
                </div>
              )}
              
              {/* Apex marker */}
              {showMarkers && apexPoint && (
                <div 
                  className={`absolute w-10 h-10 -ml-5 -mt-5 ${draggingMarker === 'apex' ? 'cursor-grabbing' : 'cursor-grab'} z-10`}
                  style={{ 
                    left: `${apexPoint.normalizedX * 100}%`, 
                    top: `${apexPoint.normalizedY * 100}%`,
                    transform: `scale(${1/zoom})`
                  }}
                  onMouseDown={(e) => handleMarkerDragStart(e, 'apex')}
                  onTouchStart={(e) => handleMarkerDragStart(e, 'apex')}
                >
                  <div className="w-full h-full rounded-full border-3 border-yellow-400 bg-yellow-400/30 flex items-center justify-center shadow-lg shadow-yellow-400/50">
                    <Circle className="w-5 h-5 text-yellow-400" />
                  </div>
                  <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[9px] text-yellow-400 font-bold whitespace-nowrap bg-black/80 px-1.5 py-0.5 rounded">
                    APEX
                  </span>
                </div>
              )}
              
              {/* Landing marker */}
              {showMarkers && landingPoint && (
                <div 
                  className={`absolute w-10 h-10 -ml-5 -mt-5 ${draggingMarker === 'landing' ? 'cursor-grabbing' : 'cursor-grab'} z-10`}
                  style={{ 
                    left: `${landingPoint.normalizedX * 100}%`, 
                    top: `${landingPoint.normalizedY * 100}%`,
                    transform: `scale(${1/zoom})`
                  }}
                  onMouseDown={(e) => handleMarkerDragStart(e, 'landing')}
                  onTouchStart={(e) => handleMarkerDragStart(e, 'landing')}
                >
                  <div className="w-full h-full rounded-full border-3 border-red-400 bg-red-400/30 flex items-center justify-center shadow-lg shadow-red-400/50">
                    <MapPin className="w-5 h-5 text-red-400" />
                  </div>
                  <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[9px] text-red-400 font-bold whitespace-nowrap bg-black/80 px-1.5 py-0.5 rounded">
                    LANDING
                  </span>
                </div>
              )}
              
              {/* TOP OVERLAY */}
              <div className="absolute top-0 left-0 right-0 z-30 bg-gradient-to-b from-black/70 to-transparent p-2">
                <div className="flex items-center justify-between">
                  <button onClick={onClose} className="text-white p-1">
                    <X className="w-5 h-5" />
                  </button>
                  
                  <span className="text-white text-xs font-medium">PS Tracer</span>
                  
                  <div className="flex items-center gap-0.5">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleZoomOut(); }}
                      disabled={zoom <= 1}
                      className="text-white p-1 disabled:opacity-40"
                    >
                      <ZoomOut className="w-4 h-4" />
                    </button>
                    <span className="text-white text-[10px] w-7 text-center">{zoom.toFixed(1)}x</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleZoomIn(); }}
                      disabled={zoom >= 4}
                      className="text-white p-1 disabled:opacity-40"
                    >
                      <ZoomIn className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* AI Status / Active marker indicator */}
                {aiPhase === 'scanning' && (
                  <div className="mt-1 bg-purple-600/90 rounded px-3 py-1.5 flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                    <span className="text-white text-xs">
                      Scanning for ball{aiAttempts > 1 ? ` (attempt ${aiAttempts}/2)` : ''}... {aiProgress}%
                    </span>
                  </div>
                )}
                {aiPhase === 'calculating' && (
                  <div className="mt-1 bg-blue-600/90 rounded px-3 py-1.5 flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                    <span className="text-white text-xs">
                      Calculating trajectory{aiAttempts > 1 ? ` (attempt ${aiAttempts}/2)` : ''}... {aiProgress}%
                    </span>
                  </div>
                )}
                {aiPhase === 'complete' && !activeMarker && (
                  <div className="mt-1 bg-green-600/90 rounded px-3 py-1.5 text-center">
                    <span className="text-white text-xs">Trajectory detected! Drag markers to adjust arc</span>
                  </div>
                )}
                {showMarkers && activeMarker && (
                  <div className="mt-1 bg-yellow-500 rounded px-3 py-1 text-center">
                    <p className="text-black text-xs font-bold">
                      TAP TO MARK {activeMarker.toUpperCase()}
                    </p>
                  </div>
                )}
              </div>
              
              {/* BOTTOM OVERLAY */}
              <div 
                className="absolute bottom-0 left-0 right-0 z-30 bg-gradient-to-t from-black/95 via-black/80 to-transparent pt-6"
                style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom, 8px))' }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Analysis data display */}
                {analysisData && aiPhase === 'complete' && (
                  <div className="mx-2 mb-2 flex items-center justify-center gap-3 text-[10px]">
                    {analysisData.ball_speed_mph > 0 && (
                      <div className="bg-blue-900/60 px-2 py-1 rounded">
                        <span className="text-blue-300">Speed:</span>
                        <span className="text-white ml-1">{analysisData.ball_speed_mph} mph</span>
                      </div>
                    )}
                    {analysisData.launch_angle_deg > 0 && (
                      <div className="bg-green-900/60 px-2 py-1 rounded">
                        <span className="text-green-300">Launch:</span>
                        <span className="text-white ml-1">{analysisData.launch_angle_deg}°</span>
                      </div>
                    )}
                    {analysisData.direction && (
                      <div className="bg-purple-900/60 px-2 py-1 rounded">
                        <span className="text-purple-300">Shape:</span>
                        <span className="text-white ml-1 capitalize">{analysisData.direction}</span>
                      </div>
                    )}
                    {analysisData.spin_estimate && analysisData.spin_estimate !== 'unknown' && (
                      <div className="bg-yellow-900/60 px-2 py-1 rounded">
                        <span className="text-yellow-300">Spin:</span>
                        <span className="text-white ml-1 capitalize">{analysisData.spin_estimate}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Filmstrip trimmer */}
                <div 
                  ref={trimContainerRef}
                  className="mx-2 relative h-10 bg-gray-900 rounded overflow-visible"
                >
                  <div className="absolute inset-0 flex rounded overflow-hidden">
                    {thumbnails.length > 0 ? (
                      thumbnails.map((thumb, i) => (
                        <div 
                          key={i} 
                          className="flex-1 h-full bg-cover bg-center"
                          style={{ backgroundImage: `url(${thumb.dataUrl})` }}
                        />
                      ))
                    ) : (
                      Array.from({ length: 10 }).map((_, i) => (
                        <div key={i} className="flex-1 h-full bg-gray-800" />
                      ))
                    )}
                  </div>
                  
                  <div 
                    className="absolute top-0 bottom-0 left-0 bg-black/70 rounded-l"
                    style={{ width: `${(trimStart / videoDuration) * 100}%` }}
                  />
                  <div 
                    className="absolute top-0 bottom-0 right-0 bg-black/70 rounded-r"
                    style={{ width: `${((videoDuration - trimEnd) / videoDuration) * 100}%` }}
                  />
                  
                  <div 
                    className="absolute top-0 bottom-0 border-y-2 border-yellow-400 pointer-events-none"
                    style={{ 
                      left: `${(trimStart / videoDuration) * 100}%`,
                      width: `${((trimEnd - trimStart) / videoDuration) * 100}%`
                    }}
                  />
                  
                  <div
                    className="absolute top-0 bottom-0 w-4 cursor-ew-resize z-10 flex items-center justify-center touch-none"
                    style={{ left: `calc(${(trimStart / videoDuration) * 100}% - 8px)` }}
                    onMouseDown={(e) => { e.preventDefault(); setDraggingHandle('start'); }}
                    onTouchStart={() => setDraggingHandle('start')}
                  >
                    <div className={`w-2 h-10 bg-yellow-400 rounded-l ${draggingHandle === 'start' ? 'scale-110' : ''}`} />
                  </div>
                  
                  <div
                    className="absolute top-0 bottom-0 w-4 cursor-ew-resize z-10 flex items-center justify-center touch-none"
                    style={{ left: `calc(${(trimEnd / videoDuration) * 100}% - 8px)` }}
                    onMouseDown={(e) => { e.preventDefault(); setDraggingHandle('end'); }}
                    onTouchStart={() => setDraggingHandle('end')}
                  >
                    <div className={`w-2 h-10 bg-yellow-400 rounded-r ${draggingHandle === 'end' ? 'scale-110' : ''}`} />
                  </div>
                  
                  <div 
                    className="absolute top-0 bottom-0 w-0.5 bg-white z-20 pointer-events-none"
                    style={{ 
                      left: `${(currentTime / videoDuration) * 100}%`,
                      boxShadow: '0 0 6px 2px rgba(255,255,255,0.7)'
                    }}
                  />
                </div>
                
                {/* Time display */}
                <div className="px-2 py-1 flex justify-between text-[10px] text-gray-400">
                  <span>{formatTime(trimStart)}</span>
                  <span className="text-white font-mono text-xs">{formatTime(currentTime)}</span>
                  <span>{formatTime(trimEnd)}</span>
                </div>
                
                {/* Marker buttons row */}
                {showMarkers && (
                  <div className="px-2 pb-1 flex items-center justify-between gap-1">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setActiveMarker(activeMarker === 'impact' ? null : 'impact')}
                        className={`${activeMarker === 'impact' ? 'bg-green-500' : impactPoint ? 'bg-green-800' : 'bg-gray-700'} text-white h-7 px-2 rounded text-[10px] flex items-center gap-1`}
                      >
                        <Target className="w-3 h-3" />
                        Impact
                      </button>
                      <button
                        onClick={() => setActiveMarker(activeMarker === 'apex' ? null : 'apex')}
                        className={`${activeMarker === 'apex' ? 'bg-yellow-500 text-black' : apexPoint ? 'bg-yellow-800' : 'bg-gray-700'} text-white h-7 px-2 rounded text-[10px] flex items-center gap-1`}
                      >
                        <Circle className="w-3 h-3" />
                        Apex
                      </button>
                      <button
                        onClick={() => setActiveMarker(activeMarker === 'landing' ? null : 'landing')}
                        className={`${activeMarker === 'landing' ? 'bg-red-500' : landingPoint ? 'bg-red-800' : 'bg-gray-700'} text-white h-7 px-2 rounded text-[10px] flex items-center gap-1`}
                      >
                        <MapPin className="w-3 h-3" />
                        Land
                      </button>
                    </div>
                    
                    <button
                      onClick={togglePlay}
                      className="w-8 h-8 rounded-full bg-white text-black flex items-center justify-center"
                    >
                      {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {(impactPoint || apexPoint || landingPoint) && (
                        <button
                          onClick={resetMarkers}
                          className="bg-gray-700 text-white h-7 px-2 rounded text-[10px]"
                        >
                          <RotateCcw className="w-3 h-3" />
                        </button>
                      )}
                      <button
                        onClick={generateTracer}
                        disabled={!canGenerate}
                        className={`${canGenerate ? 'bg-yellow-500' : 'bg-gray-600'} text-black h-7 px-3 rounded text-[10px] font-semibold flex items-center gap-1`}
                      >
                        <Sparkles className="w-3 h-3" />
                        Create
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Color picker row */}
                {showMarkers && (
                  <div className="px-2 pb-1 flex items-center justify-center gap-2">
                    {Object.entries(TRACER_COLORS).map(([key, color]) => (
                      <button
                        key={key}
                        onClick={() => setTracerColor(key)}
                        className={`w-5 h-5 rounded-full border-2 ${
                          tracerColor === key ? 'border-white scale-110' : 'border-gray-600'
                        }`}
                        style={{ backgroundColor: color.hex }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PROCESSING MODE */}
        {mode === 'processing' && (
          <div className="w-full h-full flex flex-col items-center justify-center bg-black">
            <Loader2 className="w-16 h-16 text-yellow-400 animate-spin mb-6" />
            <p className="text-xl text-white mb-2">Creating Shot Tracer...</p>
            <p className="text-gray-400">{processingStatus}</p>
            <div className="mt-6 w-64 bg-gray-800 rounded-full h-2">
              <div className="bg-yellow-400 h-2 rounded-full animate-pulse" style={{width: '60%'}}></div>
            </div>
          </div>
        )}

        {/* DONE MODE */}
        {mode === 'done' && outputUrl && (
          <div className="w-full h-full flex flex-col bg-black">
            <div className="flex items-center justify-between px-4 py-3">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Check className="w-5 h-5 text-green-400" />
                Tracer Ready!
              </h2>
              <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex-1 flex items-center justify-center p-4">
              <video
                src={outputUrl.startsWith('http') ? outputUrl : `${process.env.REACT_APP_BACKEND_URL}${outputUrl}`}
                autoPlay
                loop
                muted
                playsInline
                className="max-w-full max-h-full rounded-lg"
              />
            </div>
            
            <div className="p-4 flex gap-3">
              <button 
                onClick={resetAll}
                className="flex-1 border border-gray-700 text-white py-2 rounded-lg flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-4 h-4" /> New Video
              </button>
              <button 
                onClick={handleUseInPost}
                className="flex-1 bg-green-600 text-white py-2 rounded-lg flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" /> Use in Post
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShotTracerModal;
export { ShotTracerModal };
